import React from 'react';
import PropTypes from 'prop-types';

import intlShape from '@/shared/utils/intlShape';
import RelationName from '@/shared/components/RelationName';
import ContactIcon from '@/shared/components/ContactIcon';
import UserName from '@/shared/components/UserName';
import { FaEnvelope, FaTrashAlt, FaUndo } from 'react-icons/fa';
import { CanDelete, CanRead, CanCreate } from '@/shared/components/security';
import {
  isBelgianTenantConfiguration,
  isDutchTenantConfiguration,
} from '@/shared/TenantConfigurationConstants';
import DataCacheService from '@/shared/services/DataCacheService';
import FormattedDayMonth from '@/shared/components/FormattedDayMonth';
import messages from './relationOverviewMessages';
import Checkbox from '../../../shared/components/input/Checkbox';
import FormattedDateShort from '../../../shared/components/FormattedDateShort';
import employeeProfileMessages from '@/shared/messages/employeeProfileMessages';

export const getColumnDefinitions = tenantConfiguration =>
  [
    {
      id: 'firstName',
      message: messages.FirstName,
      sortKey: 'firstName',
      visibleByDefault: false,
    },
    {
      id: 'initials',
      message: messages.Initials,
      visibleByDefault: false,
      className: 'hidden-xs',
      sortKey: 'initials',
      available: isDutchTenantConfiguration(tenantConfiguration),
    },
    {
      id: 'infix',
      message: messages.Infix,
      visibleByDefault: false,
      className: 'hidden-xs',
      sortKey: 'infix',
      available: isDutchTenantConfiguration(tenantConfiguration),
    },
    {
      id: 'name',
      message: messages.LastName,
      visibleByDefault: false,
      className: 'hidden-xs',
      render: ({ relation }) => <span>{relation.type === 0 ? '' : relation.name}</span>,
    },
    {
      id: 'location',
      message: messages.Location,
      visibleByDefault: true,
      className: 'hidden-xs',
      render: ({ relation }) => (
        <span>
          {relation.streetNr}
          <br />
          {relation.location}
        </span>
      ),
    },
    {
      id: 'phone',
      message: messages.Phone,
      disableSorting: true,
      visibleByDefault: true,
      className: 'hidden-xs',
      render: ({ relation }) => (
        <React.Fragment>
          {relation.phone && <ContactIcon type="phone" value={relation.phone} />}
          {relation.mobile && <ContactIcon type="phone" value={relation.mobile} />}
        </React.Fragment>
      ),
    },
    {
      id: 'email',
      message: messages.Email,
      visibleByDefault: true,
      className: 'hidden-xs',
      render: ({ relation, goToInteractions }) => (
        <ContactIcon
          type="email"
          value={relation.email}
          onClick={() => goToInteractions(relation.id, true)}
        />
      ),
    },
    {
      id: 'enterpriseNumber',
      message: messages.VatOrEnterpriseNumber,
      visibleByDefault: true,
      className: 'hidden-xs',
      sortKey: 'vatNumber',
      render: ({ relation, intl }) =>
        relation.vatNumber === 'VatPending'
          ? intl.formatMessage(messages.VatPending)
          : relation.vatNumber,
    },
    {
      id: 'nationalNumber',
      message: messages.NationalNumber,
      visibleByDefault: true,
      className: 'hidden-xs',
      available: isBelgianTenantConfiguration(tenantConfiguration),
    },
    {
      id: 'kvkNumber',
      message: messages.KvkNumber,
      visibleByDefault: true,
      className: 'hidden-xs',
      available: isDutchTenantConfiguration(tenantConfiguration),
    },
    {
      id: 'fiscalNumber',
      message: messages.FiscalNumber,
      visibleByDefault: true,
      className: 'hidden-xs',
      available: isDutchTenantConfiguration(tenantConfiguration),
    },
    {
      id: 'obNumber',
      message: messages.ObNumber,
      visibleByDefault: false,
      className: 'hidden-xs',
      available: isDutchTenantConfiguration(tenantConfiguration),
    },
    {
      id: 'lbNumber',
      message: messages.LbNumber,
      visibleByDefault: false,
      className: 'hidden-xs',
      available: isDutchTenantConfiguration(tenantConfiguration),
    },
    {
      id: 'bsnNumber',
      message: messages.BsnNumber,
      visibleByDefault: true,
      className: 'hidden-xs',
      available: isDutchTenantConfiguration(tenantConfiguration),
    },
    {
      id: 'rsinNumber',
      message: messages.RsinNumber,
      visibleByDefault: false,
      className: 'hidden-xs',
      available: isDutchTenantConfiguration(tenantConfiguration),
    },
    {
      id: 'customerCode',
      message: messages.CustomerCode,
      visibleByDefault: true,
      className: 'hidden-xs',
    },
    {
      id: 'accountingNr',
      message: messages.AccountingNumber,
      visibleByDefault: true,
      className: 'hidden-xs',
    },
    {
      id: 'uniqueIdentifier',
      message: messages.UniqueIdentifier,
      visibleByDefault: true,
      className: 'hidden-xs',
    },
    {
      id: 'companyId',
      message: messages.OwnCompany,
      disableSorting: true,
      visibleByDefault: false,
      className: 'hidden-xs',
      render: ({ relation }) => (
        <span>{DataCacheService.getCompanySettings(relation.companyId)?.name}</span>
      ),
    },
    {
      id: 'relationGroup',
      message: messages.RelationGroup,
      visibleByDefault: false,
      className: 'hidden-xs',
    },
    {
      id: 'associateUser',
      message: employeeProfileMessages.Associate,
      visibleByDefault: false,
      className: 'hidden-xs',
      render: ({ relation }) => <UserName userId={relation.associateUser} />,
    },
    {
      id: 'responsibleUser',
      message: employeeProfileMessages.Responsible,
      visibleByDefault: false,
      className: 'hidden-xs',
      render: ({ relation }) => <UserName userId={relation.responsibleUser} />,
    },
    {
      id: 'seniorUser',
      message: employeeProfileMessages.SeniorUser,
      visibleByDefault: false,
      render: ({ relation }) => <UserName userId={relation.seniorUser} />,
    },
    {
      id: 'assistantUser',
      message: employeeProfileMessages.Assistant,
      visibleByDefault: false,
      className: 'hidden-xs',
      render: ({ relation }) => <UserName userId={relation.assistantUser} />,
    },
    {
      id: 'assistantBackupUser',
      message: employeeProfileMessages.AssistantBackup,
      visibleByDefault: false,
      className: 'hidden-xs',
      render: ({ relation }) => <UserName userId={relation.assistantBackupUser} />,
    },
    {
      id: 'billingUser',
      message: employeeProfileMessages.BillingUser,
      visibleByDefault: false,
      render: ({ relation }) => <UserName userId={relation.billingUser} />,
    },
    { id: 'alias', message: messages.Alias, sortKey: 'alias', visibleByDefault: false },
    {
      id: 'startDate',
      message: messages.StartDate,
      visibleByDefault: true,
      className: 'hidden-xs',
      render: ({ relation }) => <FormattedDateShort value={relation.startDate} />,
    },
    {
      id: 'active',
      message: messages.Active,
      sortKey: 'isActive',
      visibleByDefault: true,
      className: 'hidden-xs',
      render: ({ relation, intl }) =>
        relation.isActive ? intl.formatMessage(messages.Yes) : intl.formatMessage(messages.No),
    },
    {
      id: 'endDate',
      message: messages.EndDate,
      visibleByDefault: false,
      className: 'hidden-xs',
      render: ({ relation }) => <FormattedDateShort value={relation.endDate} />,
    },
    {
      id: 'endFiscalYear',
      message: messages.EndFiscalYear,
      visibleByDefault: false,
      className: 'hidden-xs',
      render: ({ relation }) => <FormattedDayMonth value={relation.endFiscalYear} />,
    },
  ].filter(x => x.available === undefined || x.available);

const RelationOverviewRow = ({
  relation,
  intl,
  handleRelationSelect,
  showDeleteRelationConfirmation,
  restoreRelation,
  goToInteractions,
  columnStatus,
}) => (
  <tbody key={relation.id} id={`RowID${relation.id}`} name={relation.name}>
    <tr>
      <td>
        <Checkbox onClick={() => handleRelationSelect(relation)} value={relation.selected} />
      </td>
      <td>
        <RelationName relation={relation} />
      </td>

      {getColumnDefinitions(DataCacheService.getTenantConfiguration()).map(
        column =>
          columnStatus[column.id] && (
            <td className="hidden-xs text-nowrap" key={column.id}>
              {column.render
                ? column.render({ relation, intl, goToInteractions })
                : relation[column.id]}
            </td>
          ),
      )}
      <td className="actions-column">
        <CanRead on="InteractionsOwn">
          <a
            className="btn btn-xs btn-icon"
            onClick={() => goToInteractions(relation.id)}
            title={intl.formatMessage(messages.OpenTimeline)}
          >
            <i className="fa fa-comments-o react-icon" />
          </a>
        </CanRead>

        <CanCreate on="InteractionsOwn">
          <a
            className="btn btn-xs btn-icon hidden-xs"
            onClick={() => goToInteractions(relation.id, true)}
            tooltip={intl.formatMessage(messages.SendEmail)}
          >
            <FaEnvelope className="react-icon" />
          </a>
        </CanCreate>
        <CanDelete on="relation">
          {relation.isActive ? (
            <a
              className="btn btn-xs btn-icon hidden-xs"
              onClick={() => showDeleteRelationConfirmation(relation)}
            >
              <FaTrashAlt className="react-icon" />
            </a>
          ) : (
            <a className="btn btn-xs btn-icon hidden-xs" onClick={() => restoreRelation(relation)}>
              <FaUndo className="react-icon" />
            </a>
          )}
        </CanDelete>
      </td>
    </tr>
  </tbody>
);

RelationOverviewRow.propTypes = {
  relation: PropTypes.shape({}).isRequired,
  intl: intlShape.isRequired,
  handleRelationSelect: PropTypes.func.isRequired,
  showDeleteRelationConfirmation: PropTypes.func.isRequired,
  restoreRelation: PropTypes.func.isRequired,
  goToInteractions: PropTypes.func.isRequired,
  columnStatus: PropTypes.objectOf(PropTypes.any).isRequired,
};

export default RelationOverviewRow;
